import { memo, useMemo, forwardRef, useState, CSSProperties } from 'react';
import classNames from 'classnames';

import { ErrorBoundary, GenericFallback } from '@/lib/error';
import { Breakpoint, useScreenSize } from '@/lib/hooks';
import { DisableInEmbedded } from '@/components/layout/disable-in-embedded';
import {
  isChatCell,
  isConversationCell,
  isConversationPart,
  isInvalidCell,
  isRecordsCell,
  isSqlCell,
} from '@/core/cell';

import { Cell, ExplorationParameters, VariableDefinition } from '../types';
import { RecordsCellView } from './records-cell-view';
import { FunnelCellView } from './funnel-cell';
import { CohortCellView } from './cohort-cell';
import { VariableCellView } from './variable-cell';
import { SqlCellView } from './sql-cell';
import { PythonCellView } from './python-cell';
import { TextCellView } from './text-cell';
import { ChatCellView } from './chat-cell';
import { ConversationCellView } from './conversation-cell';
import { ExplorationCellContextProvider } from './exploration-cell-context';
import { InvalidCellView } from './invalid-cell';
import { DraggableCell } from './exploration-layout/draggable-cell';
import { RefetchCellDataContextProvider } from './refetch-cell-data-context';
import { useConversationExplorationContext } from '../conversation/conversation-exploration-context';
import { useExplorationContext } from './exploration-context';

import styles from './exploration.module.scss';

interface ExplorationCellProps {
  parameters: ExplorationParameters;
  cell: Cell;
  selected?: boolean;
  highlighted?: boolean;
  variables: VariableDefinition[];
  isCollapsible: boolean;
  isResizable?: boolean;
  className?: string;
  style?: CSSProperties;
  canvasView?: boolean;
  height?: number;
  rowHeightOverride?: number | undefined;
  onMouseDown?: (e: React.MouseEvent) => void;
  onSelectCell?: () => void;
  onRemove?: () => void;
  setRowHeightOverride?: (height: number | undefined) => void;
}

export const ExplorationCell = memo(
  forwardRef(function ExplorationCell(
    props: ExplorationCellProps,
    ref?: React.ForwardedRef<HTMLDivElement | null>,
  ) {
    const {
      cell,
      parameters,
      selected = false,
      highlighted = false,
      variables,
      rowHeightOverride,
      isCollapsible,
      isResizable = true,
      canvasView = false,
      onSelectCell,
      onMouseDown,
      setRowHeightOverride,
    } = props;

    const { exploration, isConversationSubject } = useExplorationContext();
    const { isWithinConversation } = useConversationExplorationContext();
    const [isDraggable, setIsDraggable] = useState(false);
    const screenSize = useScreenSize();
    const desktopView = screenSize.breakpoint > Breakpoint.md;

    const classList = useMemo(
      () =>
        classNames([styles.cell, props.className], {
          [styles.selectedCell]: selected,
          [styles.highlightedCell]: highlighted,
          [styles.conversationChat]: ['chat'].includes(cell.kind),
          [styles.transparent]: ['text', 'chat', 'conversation'].includes(cell.kind) && !canvasView,
          [styles.conversation]: isConversationCell(cell),
          [styles.conversationSubject]: isConversationSubject(cell),
          [styles.conversationActive]: !isConversationPart(cell),
          [styles.conversationResponse]:
            !canvasView &&
            !isConversationCell(cell) &&
            (isConversationPart(cell) || isWithinConversation) &&
            !isChatCell(cell),
        }),
      [
        canvasView,
        cell,
        highlighted,
        isConversationSubject,
        isWithinConversation,
        props.className,
        selected,
      ],
    );

    return (
      <ErrorBoundary fallback={<GenericFallback />}>
        <ExplorationCellContextProvider
          cell={cell}
          enforcedRowCollapse={desktopView}
          isCollapsible={isCollapsible}>
          <RefetchCellDataContextProvider>
            <DraggableCell
              cellId={cell.id}
              isDraggable={!(isConversationPart(cell) || isWithinConversation) && isDraggable}
              selected={selected}
              height={props.height}
              isResizable={!canvasView && !isChatCell(cell) && cell.kind !== 'text' && isResizable}
              style={props.style}
              heightOverride={rowHeightOverride}
              setRowHeightOverride={setRowHeightOverride}>
              <div className={classList} onMouseDown={onMouseDown} ref={ref}>
                {isRecordsCell(cell) ? (
                  <RecordsCellView
                    cell={cell}
                    exploration={exploration}
                    parameters={parameters}
                    onSelectCell={onSelectCell}
                    onSetDraggable={(value) => setIsDraggable(value)}
                    height={props.height ?? rowHeightOverride}
                  />
                ) : cell.kind === 'funnel' ? (
                  <FunnelCellView
                    cell={cell}
                    exploration={exploration}
                    onSelectCell={onSelectCell}
                    onSetDraggable={(value) => setIsDraggable(value)}
                    height={props.height}
                  />
                ) : cell.kind === 'variable' ? (
                  <DisableInEmbedded>
                    <VariableCellView
                      cell={cell}
                      variables={variables}
                      onSelectCell={onSelectCell}
                      onSetDraggable={(value) => setIsDraggable(value)}
                    />
                  </DisableInEmbedded>
                ) : isSqlCell(cell) ? (
                  <SqlCellView
                    cell={cell}
                    onSelectCell={onSelectCell}
                    onSetDraggable={(value) => setIsDraggable(value)}
                    height={props.height ?? rowHeightOverride}
                  />
                ) : cell.kind === 'text' ? (
                  <TextCellView
                    cell={cell}
                    selected={selected}
                    onSelectCell={onSelectCell}
                    onSetDraggable={(value) => setIsDraggable(value)}
                  />
                ) : cell.kind === 'python' ? (
                  <PythonCellView
                    cell={cell}
                    onSelectCell={onSelectCell}
                    onSetDraggable={(value) => setIsDraggable(value)}
                  />
                ) : cell.kind === 'cohort' ? (
                  <CohortCellView
                    cell={cell}
                    exploration={exploration}
                    onSelectCell={onSelectCell}
                    onSetDraggable={(value) => setIsDraggable(value)}
                  />
                ) : isChatCell(cell) ? (
                  <ChatCellView cell={cell} exploration={exploration} />
                ) : isConversationCell(cell) ? (
                  <ConversationCellView conversationId={cell.conversationId} />
                ) : isInvalidCell(cell) ? (
                  <InvalidCellView
                    cell={cell}
                    exploration={exploration}
                    onSelectCell={onSelectCell}
                    onSetDraggable={(value) => setIsDraggable(value)}
                  />
                ) : null}
              </div>
            </DraggableCell>
          </RefetchCellDataContextProvider>
        </ExplorationCellContextProvider>
      </ErrorBoundary>
    );
  }),
);
